import { useState, useEffect } from 'react';
import { Moon, Sun } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

// Named export
export function ThemeToggle() {
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    // Check localStorage first
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') return true;
    if (savedTheme === 'light') return false;

    // Fall back to system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Update theme when toggle changes
  useEffect(() => {
    const root = window.document.documentElement;

    if (isDarkMode) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 px-2 py-1.5 bg-slate-100 dark:bg-slate-800 rounded-lg shadow-button transition-colors">
            <div className="relative">
              <Sun
                className={`h-[1.1rem] w-[1.1rem] transition-all duration-300 
                  ${isDarkMode ? 'opacity-40 scale-75' : 'opacity-100 text-amber-500'}`}
              />
              <Moon
                className={`h-[1.1rem] w-[1.1rem] absolute top-0 left-0 transition-all duration-300 
                  ${isDarkMode ? 'opacity-100 text-indigo-400' : 'opacity-0 scale-75'}`}
              />
            </div>
            <Switch
              checked={isDarkMode}
              onCheckedChange={setIsDarkMode}
              aria-label="Toggle dark mode"
              className="data-[state=checked]:bg-slate-600 dark:data-[state=checked]:bg-slate-700"
            />
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-md"
        >
          <p className="text-sm font-medium">
            {isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Default export
export default ThemeToggle;
